import {ChatboxPosition, ChatboxColors, Crisp} from 'crisp-sdk-web';
import EnvService from '../env';

export {ChatboxColors};

export type CrispColorMode = 'light' | 'dark' | 'auto';

export interface CrispAppearanceOptions {
    mode: CrispColorMode;
    theme?: ChatboxColors;
    layoutTheme?: 'default' | 'colorized';
}

export interface SetUserData {
    email?: string;
    nickname?: string;
    phone?: string;
    avatar?: string;
    company?: string;
    partnerId?: string;
}

declare global {
    interface Window {
        CRISP_RUNTIME_CONFIG?: {
            locale?: string;
        };
        $crisp?: Array<unknown>;
    }
}

/**
 * The Crisp Service.
 *
 * This service is used to interact with the Crisp SDK.
 */
class CrispService {

    /**
     * The Crisp SDK Singleton instance.
     * @private
     */
    private static crisp: typeof Crisp = Crisp;

    /**
     * Whether the chat box is opened or not.
     */
    static get isChatOpened() {
        return this.crisp.chat.isChatOpened();
    }

    /**
     * Whether the chat box is visible or not.
     */
    static get isChatVisible() {
        return this.crisp.chat.isVisible();
    }

    /**
     * Register the Crisp SDK.
     */
    static register(): void {
        window.CRISP_RUNTIME_CONFIG = {
            locale: EnvService.defaultLocale,
        };
        this.crisp.configure(EnvService.crispWebsiteID, {locale: EnvService.defaultLocale});
        this.crisp.setPosition(ChatboxPosition.Right);
        this.syncAppearance({
            mode: this.resolveDocumentColorMode(),
            theme: ChatboxColors.DeepPurple,
        });
        this.hideChatBox();
    }

    /**
     * Applies Crisp chatbox appearance via the official Web SDK config API.
     *
     * @see https://docs.crisp.chat/guides/chatbox-sdks/web-sdk/dollar-crisp/
     */
    static syncAppearance(options: CrispAppearanceOptions): void {
        this.ensureCrispQueue();
        window.$crisp!.push(['config', 'color:mode', [options.mode]]);
        if (options.theme) {
            this.crisp.setColorTheme(options.theme);
        }
        if (options.layoutTheme) {
            window.$crisp!.push(['config', 'layout:theme', [options.layoutTheme]]);
        }
    }

    /**
     * Sets the chatbox color mode (`light`, `dark`, or `auto`).
     */
    static setColorMode(mode: CrispColorMode): void {
        this.syncAppearance({mode});
    }

    /**
     * Sets the chatbox accent color theme.
     */
    static setColorTheme(theme: ChatboxColors): void {
        this.crisp.setColorTheme(theme);
    }

    /**
     * Resolves the active MUI-style color scheme from the document root.
     */
    static resolveDocumentColorMode(): CrispColorMode {
        const scheme = document.documentElement.getAttribute('data-mui-color-scheme');
        if (scheme === 'dark' || scheme === 'light') {
            return scheme;
        }
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    private static ensureCrispQueue(): void {
        window.$crisp = window.$crisp || [];
    }

    /**
     * Set the Crisp locale.
     * @param locale
     */
    static setLocale(locale: string): void {
        const code = locale.toLowerCase().slice(0, 2);

        window.CRISP_RUNTIME_CONFIG = {
            ...(window.CRISP_RUNTIME_CONFIG || {}),
            locale: code,
        };

        window.$crisp = window.$crisp || [];
        window.$crisp.push(['do', 'session:reset']);

        this.crisp.configure(EnvService.crispWebsiteID, {locale: code});
        this.syncAppearance({
            mode: this.resolveDocumentColorMode(),
            theme: ChatboxColors.DeepPurple,
        });
        this.crisp.load();
    }

    /**
     * Hide the Crisp chat box.
     */
    static hideChatBox(): void {
        this.crisp.chat.hide();
    }

    /**
     * Show the Crisp chat box.
     */
    static showChatBox(): void {
        this.crisp.chat.show();
    }

    /**
     * open the Crisp chat box.
     */
    static openChatBox(): void {
        this.crisp.chat.open();
    }

    /**
     * close the Crisp chat box.
     */
    static closeChatBox(): void {
        this.crisp.chat.close();
    }

    /**
     * Attaches a callback for when the chat box is Opened
     * @param callback
     */
    static onChatBoxOpened(callback: () => void): void {
        this.crisp.chat.onChatOpened(callback);
    }

    /**
     * Attaches a callback for when the chat box is Closed
     * @param callback
     */
    static onChatBoxClosed(callback: () => void): void {
        this.crisp.chat.onChatClosed(callback);
    }

    /**
     * Load the Crisp chat box.
     */
    static load(): void {
        return this.crisp.load();
    }

    /**
     * Show the Crisp chat box user information.
     * @param data
     */
    static setUser(data: SetUserData) {
        if (data.nickname) {
            this.crisp.user.setNickname(data.nickname);
            this.crisp.session.setData({
                nickname: data.nickname,
            });
        }
        if (data.email) {
            this.crisp.user.setEmail(data.email);
            this.crisp.session.setData({
                email: data.email,
            });
        }
        if (data.phone) {
            this.crisp.user.setPhone(data.phone);
            this.crisp.session.setData({
                phone: data.phone,
            });
        }
        if (data.avatar) {
            this.crisp.user.setAvatar(data.avatar);
            this.crisp.session.setData({
                avatar: data.avatar,
            });
        }
        if (data.company) {
            this.crisp.user.setCompany(data.company, {});
            this.crisp.session.setData({
                company: data.company,
            });
        }
        if (data.partnerId) {
            this.crisp.session.setData({
                partnerId: data.partnerId,
            });
        }
        this.crisp.session.setData({
            platform: EnvService.websiteName,
            platformURL: window.location.href,
        });
    }
}

export default CrispService;
